/// <amd-module name="VSS/Features/Frame/ContributedPivotBarItemProvider" />

import * as React from "react";

import { MakeOptional } from "VSS/Core/Util/Types";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { WrappedComponent } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssContributedPivotBarItem, IVssPivotBarItemProvider } from "VSSUI/Components/PivotBar/PivotBar.Props";
import { css } from "VSSUI/Util";
import { IVssContributableTabService } from "./IVssContributableTabService";

/**
 * Contribution properties to use when contributing a pivot with new platform content.
 */
export interface INewPlatformPivotBarItemSpec {
    /**
     * Optional dependencies that need to be resolved when rendering this component.
     */
    dependencies?: string[];
    /**
     * Contribution id of the content ot display.
     */
    componentType?: string;
    /**
     * Props to pass to the component
     */
    componentProperties?: { [prop: string]: any };
}

/**
 * Contribution properties to use when contributing a pivot with old platform content.
 */
export interface IOldPlatformPivotBarItemSpec {
    /**
     * For legacy contributions, load these contributions before attempting to load the moduleContent.
     */
    dependencies?: string[];
    /**
     * Contribution id of the content to display, this can be loaded dynamically.
     */
    moduleContent?: string;
    /**
     * Old platform content spec.
     */
    content?: any;
    /**
     * Path to a web page (typically provided by an extension) to load as the content of the pivot.
     */
    uri?: string;
}

/**
 * Represents the properties of a PivotBarItem/tab that might be specified in a .json file.
 */
export interface IPivotBarItemSpec extends INewPlatformPivotBarItemSpec, IOldPlatformPivotBarItemSpec, IVssContributedPivotBarItem {
    /**
     * The name to display for the pivot.
     */
    name?: string;

    /**
     * If true, use a fast page switch when switching to this pivot (a url must be supplied by the pivot).
     */
    fpsRequired?: boolean;

    /**
     * Id of the route to use to compute the url for this pivot
     */
    routeId?: string;

    /**
     * @deprecated Optionally decide whether or not the item should actually appear in the PivotBar.
     */
    hidden?: boolean;

    /**
     * Optional route values to use along with the supplied route id when computing this pivot's url
     */
    routeValues?: { [key: string]: string };
}

export interface IPivotContributionWithId {
    id: string;
    pivotContribution: IPivotBarItemSpec;
}

export type GetContextType = (id: string, isProvider?: boolean) => any;
export type GetMutatedPivotBarItemType = (contribution: IPivotContributionWithId, item: IPivotBarItemSpec) => IPivotBarItemSpec | null;

export interface IContributedPivotBarItemProviderOptions {
    /** the page context */
    pageContext: IVssPageContext;
    /** Find tabs that target this id. Specify exactly one of this or contributionPromise. */
    tabGroupIds?: string[];
    /** Contributed pivots will be provided by this promise. Specify exactly one of this or tabGroupId. */
    contributions?: IPivotContributionWithId[];
    /** Filter out contributions that are not of this type. Defaults to ms.vss-web.tab. */
    tabType?: string;
    /** Callback to get the context object to pass to the contribution. */
    getContext?: GetContextType;
    /** Additional props to pass to the contributed components. */
    props?: any;
    /** Dependencies for contributions */
    dependencies?: string[];
    /** Component to display while the pivot contents are loading */
    loadingComponent?: () => JSX.Element;
    /** Optionally change pivotbar item, return null if you would want to hide the item */
    getMutatedPivotBarItem?: GetMutatedPivotBarItemType;
    /** If true, perform an FPS (fast page switch) when switching between any two tabs. */
    fpsRequired?: boolean;
}

/**
 * Creates an IVssContributedPivotBarItem from a given pivot bar item spec (tab contribution properties)
 *
 * @param pageContext Page context
 * @param pivotContribution Pivot bar item spec (i.e. tab contribution properties)
 * @param renderMethod Method to use to render the pivot when selected
 */
export function createPivotBarItem(
    pageContext: IVssPageContext,
    pivotContribution: IPivotBarItemSpec,
    renderMethod: (className?: string) => JSX.Element,
    fpsRequired: boolean
): IVssContributedPivotBarItem {
    const pivot: IVssContributedPivotBarItem = {
        id: pivotContribution.id || pivotContribution.itemKey || "",
        text: pivotContribution.text || pivotContribution.name || "",
        itemKey: pivotContribution.itemKey,
        groupKey: pivotContribution.groupKey,
        order: pivotContribution.order,
        render: renderMethod,
        hidden: pivotContribution.hidden,
        iconProps: pivotContribution.iconProps
    };

    if (pivotContribution.routeId) {
        const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
        pivot.url = locationService.routeUrl(pivotContribution.routeId, pivotContribution.routeValues);
    }

    if (fpsRequired || pivotContribution.fpsRequired) {
        pivot.onBeforePivotChange = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | null, pivotKey: string, url?: string) => {
            if (url) {
                FastPageSwitch(pageContext, url, true);
                ev && ev.preventDefault();
                return false;
            } else {
                return true;
            }
        };
    }

    return pivot;
}

/**
 * Finds contributed pivots and provides them to a PivotBar.
 */
export class ContributedPivotBarItemProvider implements IVssPivotBarItemProvider {
    private _pageContext: IVssPageContext;
    private _tabGroupIds?: string[];
    private _contributions?: IPivotContributionWithId[];
    private _tabType: string;
    private _getContext: GetContextType | undefined;
    private _additionalProps: any;
    private _dependencies: string[];
    private _loadingComponent?: () => JSX.Element;
    private _getMutatedPivotBarItem: GetMutatedPivotBarItemType | undefined;
    private _loadedPivots: string[];
    private _fpsRequired: boolean;

    public constructor(options: IContributedPivotBarItemProviderOptions) {
        this._pageContext = options.pageContext;
        if (options.tabGroupIds) {
            this._tabGroupIds = options.tabGroupIds;
        } else if (options.contributions) {
            this._contributions = options.contributions;
        }
        this._tabType = options.tabType || "ms.vss-web.tab";
        this._getContext = options.getContext;
        this._additionalProps = options.props;
        this._dependencies = options.dependencies || [];
        this._loadingComponent = options.loadingComponent;
        this._getMutatedPivotBarItem = options.getMutatedPivotBarItem;
        this._loadedPivots = [];
        this._fpsRequired = !!options.fpsRequired;
    }

    public loadItems(itemsUpdated: (items: IVssContributedPivotBarItem[]) => void): void {
        const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        const children = this._getContributions(contributionService);
        const pivots: IVssContributedPivotBarItem[] = [];
        for (const child of children) {
            const childId = child.id;
            let pivotContribution: IPivotBarItemSpec | null = child.pivotContribution;

            if (pivotContribution) {
                if (this._getMutatedPivotBarItem) {
                    pivotContribution = this._getMutatedPivotBarItem(child, pivotContribution);
                }

                if (pivotContribution) {
                    const renderMethod = this._getRenderMethod(pivotContribution, childId);
                    if (renderMethod) {
                        const contributedPivot = this._makeContributedPivotBarItem(pivotContribution, childId, renderMethod);
                        if (this._loadedPivots.indexOf(contributedPivot.id) === -1) {
                            this._loadedPivots.push(contributedPivot.id);
                            pivots.push(contributedPivot);
                        }
                    } else {
                        console.warn(`Cannot render contributed pivot ${childId}.`);
                    }
                }
            }
        }

        if (pivots.length > 0) {
            itemsUpdated(pivots);
        }

        let allProviderIds: string[] = [];
        (this._tabGroupIds || []).forEach(tabGroupId => {
            allProviderIds = allProviderIds.concat(contributionService.getContributionChildren(tabGroupId, "ms.vss-web.tab-provider") || []);
        });

        allProviderIds.forEach(providerId => {
            contributionService
                .getService<IVssContributableTabService>(providerId)
                .then((tabContributionService: IVssContributableTabService | undefined) => {
                    if (tabContributionService) {
                        const context = this._getContext ? this._getContext(providerId, true) : undefined;
                        tabContributionService.loadItems((items: IVssContributedPivotBarItem[]): void => {
                            const updateItems: IVssContributedPivotBarItem[] = [];
                            for (let contributedPivot of items) {
                                if (this._loadedPivots.indexOf(contributedPivot.id) === -1) {
                                    this._loadedPivots.push(contributedPivot.id);
                                    updateItems.push(contributedPivot);
                                }
                            }

                            if (updateItems.length > 0) {
                                itemsUpdated(updateItems);
                            }
                        }, context);
                    }
                });
        });
    }

    private _getContributions(contributionService: IVssContributionService): IPivotContributionWithId[] {
        if (this._tabGroupIds) {
            return this._queryContributions(contributionService);
        }
        if (this._contributions) {
            return this._contributions;
        }
        return [];
    }

    private _queryContributions(contributionService: IVssContributionService): IPivotContributionWithId[] {
        const results: IPivotContributionWithId[] = [];

        for (const tabGroupId of this._tabGroupIds!) {
            const children = contributionService.getContributionChildren(tabGroupId, this._tabType);
            if (children) {
                const contributions = children
                    .map(childId => ({
                        id: childId,
                        pivotContribution: contributionService.getContribution<IPivotBarItemSpec>(childId)
                    }))
                    .filter(contributionWithId => !!contributionWithId.pivotContribution) as { id: string; pivotContribution: IPivotBarItemSpec }[];
                if (contributions.length > 0) {
                    results.push(...contributions);
                }
            }
        }

        return results;
    }

    private _makeContributedPivotBarItem(
        pivotContribution: IPivotBarItemSpec,
        id: string,
        renderMethod: (className?: string) => JSX.Element
    ): IVssContributedPivotBarItem {
        const pivot = createPivotBarItem(this._pageContext, pivotContribution, renderMethod, this._fpsRequired);
        pivot.id = id;
        pivot.sourceContribution = { id, ...(pivotContribution as MakeOptional<IPivotBarItemSpec, "id">) };
        return pivot;
    }

    private _getRenderMethod(pivotContribution: IPivotBarItemSpec, contributionId: string): ((className?: string) => JSX.Element) | null {
        const moduleContent = pivotContribution.moduleContent;
        if (moduleContent) {
            // modulecontent is the id of a contribution to render
            if (this._fpsRequired || pivotContribution.fpsRequired) {
                // when we use an fps to switch to the pivot, we want to use ExternalHub because it
                // handles loading all the fps data into the correct old platform places
                return (className?: string) => this._renderExternalHub(moduleContent, className, pivotContribution.dependencies);
            } else {
                return (className?: string) => this._renderByContributionId(moduleContent, className, pivotContribution.dependencies);
            }
        }

        if (pivotContribution.uri) {
            return (className?: string) =>
                this._renderExternalContent(
                    pivotContribution.id || contributionId,
                    className,
                    pivotContribution.dependencies,
                    pivotContribution.componentProperties
                );
        }

        if (pivotContribution.id || pivotContribution.uri || pivotContribution.content) {
            return (className?: string) =>
                this._renderByContributionId(pivotContribution.id || contributionId, className, pivotContribution.dependencies);
        }

        if (pivotContribution.componentType) {
            const componentType = pivotContribution.componentType;
            return (className?: string) => this._renderComponent(pivotContribution, contributionId, componentType, className);
        }

        return null;
    }

    private _renderComponent(pivotContribution: IPivotBarItemSpec, contributionId: string, componentType: string, className?: string): JSX.Element {
        let props = pivotContribution.componentProperties ? { ...pivotContribution.componentProperties } : {};
        props = {
            ...props,
            ...this._additionalProps,
            ...(this._getContext ? { ...this._getContext(contributionId) } : {}),
            className: css(props.className, className)
        };

        return (
            <WrappedComponent
                dependencies={this._dependencies.concat(pivotContribution.dependencies || [])}
                key={contributionId}
                wrappedProps={props}
                wrappedType={componentType}
            />
        );
    }

    private _renderExternalContent(
        contributionId: string,
        className?: string,
        dependencies?: string[],
        componentProperties?: { [prop: string]: any }
    ): JSX.Element {
        return (
            <WrappedComponent
                dependencies={this._dependencies.concat(dependencies || []).concat(["ms.vss-web.ext-content"])}
                key={contributionId}
                wrappedType="ExternalContent"
                wrappedProps={{
                    contributionId,
                    ...componentProperties,
                    ...this._additionalProps,
                    initialHandshakeData: this._getContext ? { ...this._getContext(contributionId) } : {},
                    className: css(className, "flex-grow")
                }}
            />
        );
    }

    private _renderExternalHub(contributionId: string, className?: string, dependencies?: string[]): JSX.Element {
        return (
            <WrappedComponent
                key={contributionId}
                dependencies={["ms.vss-web.legacy-content", "ms.vss-web.legacy-vss-ui-content"]}
                wrappedType="legacy"
                wrappedProps={{
                    className: className,
                    componentType: "legacyHubShimComponent",
                    dependentContributionIds: this._dependencies.concat(dependencies || []),
                    modules: ["VSSPreview/Flux/Components/ExternalHubShim"],
                    contributionId: contributionId
                }}
            />
        );
    }

    private _renderByContributionId(contributionId: string, className?: string, dependencies?: string[]): JSX.Element {
        let context = undefined;
        if (this._getContext) {
            context = this._getContext(contributionId);
        }

        return (
            <WrappedComponent
                key={contributionId}
                dependencies={["ms.vss-web.legacy-content", "ms.vss-web.legacy-vss-ui-content"]}
                wrappedType="legacy"
                wrappedProps={{
                    componentType: "vssLegacyContributionContentHost",
                    modules: ["VSSPreview/Providers/ContentHost"],
                    dependentContributionIds: this._dependencies.concat(dependencies || []),
                    wrappedProps: {
                        className: className,
                        context: context,
                        contribution: contributionId,
                        loadingComponent: this._loadingComponent
                    },
                    loadingComponent: this._loadingComponent
                }}
            />
        );
    }
}
